import { CommonModule } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormArray,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from '@angular/forms'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { ClientService } from '../client.service'
import { MemberService } from '../../member-client/member.service'
import { ToastrService } from 'ngx-toastr'
import { ActivatedRoute, Router } from '@angular/router'
import { MasterCommonServices } from '../../quotation/master.common.service'

@Component({
  selector: 'app-client-master',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, MatProgressBarModule, FormsModule],
  templateUrl: './add-client.component.html',
  styleUrls: ['./add-client.component.css'],
})
export class AddClientComponent implements OnInit {
  addClientForm: FormGroup = new FormGroup({})
  isLoading: boolean = false
  submitted: boolean = false
  mode: string = 'add'
  clientData: any
  isSubmitDisabled: boolean = false
  members: any[] = []
  expandedMembers: { [key: number]: boolean } = {}
  memberSubmitted: boolean = false

 constructor(
  private formBuilder: FormBuilder,
  private clientService: ClientService,
  private memberService: MemberService,
  private toastr: ToastrService,
  private router: Router,
  public masterCommonService: MasterCommonServices,
) {
  this.initializeForm()

  const navigation = this.router.getCurrentNavigation()
  console.log(navigation)
  if (navigation?.extras.state) {
    const state = navigation.extras.state

    this.mode = state['mode'] || 'add'

    if (this.mode === 'update' || this.mode === 'view') {
      this.clientData = state['client']

      this.addClientForm.patchValue({
        id: this.clientData.id,
        companyName: this.clientData.companyName,
        address: this.clientData.address,
        region: this.clientData.region,
        website: this.clientData.website,
        addedOn: this.clientData.addedOn,
        modifiedOn: this.clientData.modifiedOn,
      })

      this.addClientForm.markAsPristine()
      this.isSubmitDisabled = true

      this.loadMembersForClient(this.clientData.id)
    }
  }
}

  ngOnInit(): void {
  this.addClientForm.statusChanges.subscribe(() => {
    this.updateSubmitButtonState()
  })
}

  loadMembersForClient(clientId: number): void {
    this.memberService.getAllMembers().subscribe({
      next: (allMembers: any[]) => {
        console.log('Client ID:', clientId)
console.log('All Members:', allMembers)
        this.members = allMembers.filter((m) => m.clientId === clientId)
        // Initialize expanded state - all collapsed by default
        this.members.forEach((_, index) => {
          this.expandedMembers[index] = false
        })
        console.log('Filtered Members:', this.members)
      },
      error: (error: any) => {
        console.error('Error loading members:', error)
      },
    })
  }

  updateSubmitButtonState(): void {
    // Disable submit button if form is pristine (not modified)
    this.isSubmitDisabled = this.addClientForm.pristine
  }

  initializeForm() {
    this.addClientForm = this.formBuilder.group({
      id: [null],
      companyName: ['', Validators.required],
      address: ['', Validators.required],
      region: ['', Validators.required],
      website: [''],
      addedOn: [''],
      modifiedOn: [''],
    })
  }

  submit() {
    this.submitted = true
    if (this.addClientForm.invalid) {
      return
    }
    this.isLoading = true
    let payload = this.addClientForm.value
    payload['modifiedOn'] = new Date(
      new Date().getTime() - new Date().getTimezoneOffset() * 60000,
    ).toISOString()
    if (this.mode === 'update' || this.mode === 'update') {
      this.clientService.updateClient(payload).subscribe({
        next: (response: any) => {
          this.isLoading = false
          this.toastr.success(
            'Client Updated Successfully!!',
            'Updation Successful',
          )
          this.masterCommonService.getAllClients()
          // Mark form as pristine and disable submit button
          this.addClientForm.markAsPristine()
          this.isSubmitDisabled = true
          this.submitted = false
        },
        error: (error: any) => {
          this.isLoading = false
          this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
        },
      })
    } else {
      payload['addedOn'] = new Date(
        new Date().getTime() - new Date().getTimezoneOffset() * 60000,
      ).toISOString()
      this.clientService.addClient(payload).subscribe({
        next: (response: any) => {
          this.isLoading = false
          this.masterCommonService.getAllClients()
          this.toastr.success(
            'Client Added Successfully!!',
            'Creation Successful',
          )
          // Change mode to edit, populate ID, and disable submit button
          this.mode = 'update'
          this.addClientForm.patchValue({
            id: response?.id,
            addedOn: response?.addedOn
          })
          this.addClientForm.markAsPristine()
          this.isSubmitDisabled = true
          this.submitted = false
        },
        error: (error: any) => {
          this.isLoading = false
          this.toastr.error(error.statusText, 'Error', { timeOut: 15000,
  closeButton: true,
  progressBar: true, })
        },
      })
    }
  }

  // Member Management Methods
  addMember(): void {
    const newMember = {
      id: null,
      clientId: this.addClientForm.get('id')?.value,
      memberName: '',
      email: '',
      mobile: '',
      isWhatsApp: false,
      skypeId: '',
      telephone: '',
      addedOn: null,
      modifiedOn: null,
      isNew: true,
      isEditing: true,
    }
    this.members.push(newMember)
    this.expandedMembers[this.members.length - 1] = true
  }

  toggleMemberExpanded(index: number): void {
    this.expandedMembers[index] = !this.expandedMembers[index]
  }

  saveMember(member: any): void {
    this.memberSubmitted = true
    
    // Validate required fields
    if (!member.memberName || !member.email || !member.mobile) {
      this.toastr.error('Please fill all required fields', 'Validation Error')
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(member.email)) {
      this.toastr.error('Please enter a valid email', 'Validation Error')
      return
    }

    this.isLoading = true

    if (member.isNew) {
      member.addedOn = new Date(
        new Date().getTime() - new Date().getTimezoneOffset() * 60000,
      ).toISOString()
      this.memberService.addMember(member).subscribe({
        next: (response: any) => {
          this.isLoading = false
          member.id = response?.id
          member.isNew = false
          member.isEditing = false
          this.memberSubmitted = false
          this.toastr.success('Member Added Successfully!', 'Success')
        },
        error: (error: any) => {
          this.isLoading = false
          this.toastr.error(
            error.statusText || 'Error adding member',
            'Error',
            { timeOut: 15000, closeButton: true, progressBar: true }
          )
        },
      })
    } else {
      member.modifiedOn = new Date(
        new Date().getTime() - new Date().getTimezoneOffset() * 60000,
      ).toISOString()
      this.memberService.updateMember(member).subscribe({
        next: (response: any) => {
          this.isLoading = false
          member.isEditing = false
          this.memberSubmitted = false
          this.toastr.success('Member Updated Successfully!', 'Success')
        },
        error: (error: any) => {
          this.isLoading = false
          this.toastr.error(
            error.statusText || 'Error updating member',
            'Error',
            { timeOut: 15000, closeButton: true, progressBar: true }
          )
        },
      })
    }
  }

  editMember(member: any): void {
    member.isEditing = true
    this.expandedMembers[this.members.indexOf(member)] = true
  }

  cancelMember(index: number, member: any): void {
    if (member.isNew) {
      this.members.splice(index, 1)
      delete this.expandedMembers[index]
    } else {
      member.isEditing = false
      this.memberSubmitted = false
    }
  }

  deleteMember(index: number, member: any): void {
    if (confirm('Are you sure you want to delete this member?')) {
      this.isLoading = true
      this.memberService.deleteMember(member).subscribe({
        next: () => {
          this.isLoading = false
          this.members.splice(index, 1)
          delete this.expandedMembers[index]
          this.toastr.success('Member Deleted Successfully!', 'Success')
        },
        error: (error: any) => {
          this.isLoading = false
          this.toastr.error(
            error.statusText || 'Error deleting member',
            'Error',
            { timeOut: 15000, closeButton: true, progressBar: true }
          )
        },
      })
    }
  }
}
